/**
 * @module services/snomed-coding-form
 * @description UC-REG-288 — Helpers puros del esquema de codificación SNOMED CT
 *              en el formulario "Orden de imagen". Sin estado ni HTTP: se
 *              testean en Jest y los usa `Indications.vue` (armado del
 *              formulario) y `services/indication.js` (serialización).
 *
 *              Con modo `catalog` (default, tenant sin flag u org sin
 *              parametrización) NINGUNA función altera el formulario: el
 *              selector del catálogo propio (`imagesSelect`) queda intacto.
 */

export const SNOMED_SYSTEM = "http://snomed.info/sct";

/** @description Modos del esquema (parametrización PRESCRIPTION_IMAGES_MODE). */
export const SNOMED_MODES = ["catalog", "snomed-search", "snomed-ai"];

/** @description Campo que reemplaza a `image` cuando el esquema está activo. */
export const CODING_FIELD_KEY = "coding";

/**
 * @description Definición del campo de codificación (mismo shape que los
 *              `defaultValues` de `INDICATIONS_TYPES`).
 * @returns {Object}
 */
export function codingFieldDefinition() {
  return {
    friendlyName: "Estudio",
    value: null,
    inputType: "snomedCoding",
    required: true,
  };
}

/**
 * @description ¿El modo activa el esquema SNOMED?
 * @param {string} mode
 * @returns {boolean}
 */
export function isSnomedMode(mode) {
  return mode === "snomed-search" || mode === "snomed-ai";
}

/**
 * @description Reemplaza el campo `image` (selector del catálogo propio) por
 *              `coding` en una indicación/formulario de imágenes, conservando
 *              el orden de los campos. Devuelve el MISMO objeto si el modo es
 *              `catalog`, el tipo no es `images` o no hay campo `image`.
 * @param {Object} indication - objeto con `type` y `defaultValues` (objeto por clave)
 * @param {string} effectiveMode - 'catalog' | 'snomed-search' | 'snomed-ai'
 * @returns {Object} la misma referencia (mutada) o sin cambios
 */
export function applyImagesMode(indication, effectiveMode) {
  if (
    !indication ||
    indication.type !== "images" ||
    !isSnomedMode(effectiveMode)
  ) {
    return indication;
  }
  const dv = indication.defaultValues;
  if (!dv || Array.isArray(dv) || !dv.image) return indication;
  const rebuilt = {};
  Object.keys(dv).forEach((key) => {
    if (key === "image") {
      rebuilt[CODING_FIELD_KEY] = codingFieldDefinition();
    } else {
      rebuilt[key] = dv[key];
    }
  });
  indication.defaultValues = rebuilt;
  return indication;
}

/**
 * @description ¿El valor del campo está resuelto (concepto confirmado o texto
 *              libre asumido)? Es lo que hace válido el campo requerido.
 * @param {Object|null} value - { text, sctid, display, status, confidence, decisionId }
 * @returns {boolean}
 */
export function isCodingResolved(value) {
  if (!value || typeof value !== "object") return false;
  if (value.status === "coded") return Boolean(value.sctid);
  if (value.status === "pending")
    return Boolean(value.text && value.text.trim());
  return false;
}

/**
 * @description Texto a mostrar para un valor de codificación (resúmenes,
 *              historia clínica).
 * @param {Object|null} value
 * @returns {string}
 */
export function codingDisplay(value) {
  if (!value || typeof value !== "object") return "";
  if (value.status === "coded" && value.display) {
    return value.sctid
      ? `${value.display} (SNOMED CT ${value.sctid})`
      : value.display;
  }
  if (value.status === "pending" && value.text) {
    return `${value.text} (pendiente de codificar)`;
  }
  return value.display || value.text || "";
}

/**
 * @description Convierte el valor del campo en los campos que espera
 *              `POST /indications`: `coding` (subdocumento) + `description`
 *              (texto que imprime el PDF cuando no hay `image`).
 * @param {Object|null} value
 * @returns {{coding: Object, description: string}|null} null si no está resuelto
 */
export function codingValueToPayload(value) {
  if (!isCodingResolved(value)) return null;
  if (value.status === "coded") {
    return {
      coding: {
        system: SNOMED_SYSTEM,
        code: String(value.sctid),
        display: value.display || "",
        status: "coded",
        ...(typeof value.confidence === "number" && {
          confidence: value.confidence,
        }),
        ...(value.decisionId && { decision: value.decisionId }),
      },
      description: value.display || value.text || "",
    };
  }
  return {
    coding: {
      system: SNOMED_SYSTEM,
      status: "pending",
      ...(value.decisionId && { decision: value.decisionId }),
    },
    description: value.text.trim(),
  };
}
